import { ComponentPropsWithoutRef, ForwardedRef, forwardRef } from 'react';
import * as Dialog from 'components/dialog';
import { twMerge } from 'tailwind-merge';

interface SeatingFrameContainerProps extends ComponentPropsWithoutRef<'div'> {}
const SeatingFrameContainer = forwardRef(
    (
        { className, ...restProps }: SeatingFrameContainerProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => (
        <Dialog.Root open modal={false}>
            <Dialog.Portal>
                <Dialog.Content variant="fullscreen" className="animate-none">
                    <Dialog.Description className="sr-only">
                        Table seating builder interface
                    </Dialog.Description>
                    <div
                        className={twMerge('flex h-full overflow-hidden bg-white', className)}
                        ref={forwardedRef}
                        {...restProps}
                    />
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    ),
);
SeatingFrameContainer.displayName = 'SeatingFrameContainer';

interface SeatingFrameTitleProps extends ComponentPropsWithoutRef<'div'> {}
const SeatingFrameTitle = forwardRef(
    (
        { className, ...restProps }: SeatingFrameTitleProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => (
        <Dialog.Title ref={forwardedRef} className={twMerge('sr-only', className)} {...restProps} />
    ),
);
SeatingFrameTitle.displayName = 'SeatingFrameTitle';

interface SeatingFrameLeftPanelProps extends ComponentPropsWithoutRef<'div'> {}
const SeatingFrameLeftPanel = forwardRef(
    (
        { className, ...restProps }: SeatingFrameLeftPanelProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => (
        <div
            className={twMerge('flex h-full w-80 flex-shrink-0 flex-col bg-white', className)}
            ref={forwardedRef}
            {...restProps}
        />
    ),
);
SeatingFrameLeftPanel.displayName = 'SeatingFrameLeftPanel';

interface SeatingFrameDividerProps extends ComponentPropsWithoutRef<'div'> {}
const SeatingFrameDivider = forwardRef(
    (
        { className, ...restProps }: SeatingFrameDividerProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => (
        <div
            className={twMerge('h-full w-px bg-gray-200', className)}
            ref={forwardedRef}
            {...restProps}
        />
    ),
);
SeatingFrameDivider.displayName = 'SeatingFrameDivider';

interface SeatingFrameRightPanelProps extends ComponentPropsWithoutRef<'div'> {}
const SeatingFrameRightPanel = forwardRef(
    (
        { className, children, ...restProps }: SeatingFrameRightPanelProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => (
        <div
            className={twMerge(
                'flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-gray-50',
                className,
            )}
            ref={forwardedRef}
            {...restProps}
        >
            <div className="flex h-full flex-col px-6">
                <div className="flex h-full flex-col rounded-lg">{children}</div>
            </div>
        </div>
    ),
);
SeatingFrameRightPanel.displayName = 'SeatingFrameRightPanel';

interface SeatingFrameRightHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const SeatingFrameRightHeader = forwardRef(
    (
        { className, ...restProps }: SeatingFrameRightHeaderProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => (
        <div
            className={twMerge(
                'flex flex-shrink-0 items-center justify-between rounded-t-lg border-b border-b-gray-100 bg-gray-50 py-3',
                className,
            )}
            ref={forwardedRef}
            {...restProps}
        />
    ),
);
SeatingFrameRightHeader.displayName = 'SeatingFrameRightHeader';

interface SeatingFrameRightContentProps extends ComponentPropsWithoutRef<'div'> {}
const SeatingFrameRightContent = forwardRef(
    (
        { className, ...restProps }: SeatingFrameRightContentProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => (
        <div
            className={twMerge('h-full min-h-0 flex-grow overflow-hidden rounded-b-lg', className)}
            ref={forwardedRef}
            {...restProps}
        />
    ),
);
SeatingFrameRightContent.displayName = 'SeatingFrameRightContent';

const Root = SeatingFrameContainer;
const Title = SeatingFrameTitle;
const LeftPanel = SeatingFrameLeftPanel;
const Divider = SeatingFrameDivider;
const RightPanel = SeatingFrameRightPanel;
const RightHeader = SeatingFrameRightHeader;
const RightContent = SeatingFrameRightContent;

export {
    SeatingFrameContainer,
    SeatingFrameTitle,
    SeatingFrameLeftPanel,
    SeatingFrameDivider,
    SeatingFrameRightPanel,
    SeatingFrameRightHeader,
    SeatingFrameRightContent,
    //
    Root,
    Title,
    LeftPanel,
    Divider,
    RightPanel,
    RightHeader,
    RightContent,
};
